export const PROFILE_ACTIONS = {
  SET_PROFILE_DATA: 'SET_PROFILE_DATA',
  UPDATE_FIELD: 'UPDATE_FIELD',
  UPDATE_NESTED_FIELD: 'UPDATE_NESTED_FIELD',
  SET_PREVIEW_IMAGE: 'SET_PREVIEW_IMAGE',
  SET_DIRTY: 'SET_DIRTY',
  SET_SAVING: 'SET_SAVING',
  SET_SAVE_SUCCESS: 'SET_SAVE_SUCCESS',
  SET_ACTIVE_TAB: 'SET_ACTIVE_TAB',
  RESET_PROFILE: 'RESET_PROFILE',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_FIELD_ERRORS: 'SET_FIELD_ERRORS',
  CLEAR_FIELD_ERROR: 'CLEAR_FIELD_ERROR',
  SET_TEMP_IMAGE: 'SET_TEMP_IMAGE',
  MERGE_CHANGED_FIELDS: 'MERGE_CHANGED_FIELDS', 
  SET_PENDING_IMAGE_FILE: 'SET_PENDING_IMAGE_FILE',
};

export const initialProfileState = {
  userData: {
    username: "",
    name: "",
    bio: "",
    profileImage: null,
    location: "",
    website: "",
    company: "",
    position: "",
    experience: "",
    education: "",
    gender: "",
    social: {
      x: "",
      instagram: "",
    },
  },
  originalData: null,
  changedFields: {},
  previewImage: null,
  isDirty: false,
  isSaving: false,
  saveSuccess: false,
  activeTab: "profile",
  error: null,
  fieldErrors: {},
  tempImage: null,
};

export function profileReducer(state, action) {
  switch (action.type) {
    case PROFILE_ACTIONS.SET_PROFILE_DATA:
      return {
        ...state,
        userData: { ...state.userData, ...action.payload },
        originalData: action.payload,
        changedFields: {},
        previewImage: action.payload.profileImage || null,
        isDirty: false,
        error: null,
        fieldErrors: {},
      };

    // ✅ NEW ACTION: Merge changed fields from backend response
    // Backend returns only the fields it updated, we merge them with current state
    case PROFILE_ACTIONS.MERGE_CHANGED_FIELDS:
      const changedResponse = action.payload;
      
      // Build merged userData
      const mergedUserData = { ...state.userData };

      // Merge regular fields
      for (const [key, value] of Object.entries(changedResponse)) {
        if (key === "social" || key === "privacySettings") {
          // Handle nested objects
          if (key === "social") {
            mergedUserData.social = {
              ...mergedUserData.social,
              ...value,
            };
          } else if (key === "privacySettings") {
            mergedUserData.privacySettings = {
              ...mergedUserData.privacySettings,
              ...value,
            };
          }
        } else {
          // Regular field
          mergedUserData[key] = value;
        }
      }

      // Update originalData to reflect new state
      const newOriginalData = {
        ...state.originalData,
        ...changedResponse,
      };

      return {
        ...state,
        userData: mergedUserData,
        originalData: newOriginalData,
        changedFields: {}, // ✅ Clear changed fields after successful save
        isDirty: false, // ✅ Mark as clean
        error: null,
        fieldErrors: {},
      };

    case PROFILE_ACTIONS.UPDATE_FIELD:
      const newValue = action.payload.value;
      const fieldName = action.payload.name;
      const originalValue = state.originalData?.[fieldName];
      
      const isChanged = newValue !== originalValue;
      const newChangedFields = { ...state.changedFields };
      
      if (isChanged) {
        newChangedFields[fieldName] = newValue;
      } else {
        delete newChangedFields[fieldName];
      }

      // Clear field error when user starts typing
      const newFieldErrors = { ...state.fieldErrors };
      delete newFieldErrors[fieldName];

      return {
        ...state,
        userData: {
          ...state.userData,
          [fieldName]: newValue
        },
        changedFields: newChangedFields,
        fieldErrors: newFieldErrors,
        isDirty: Object.keys(newChangedFields).length > 0,
        error: null
      };

    case PROFILE_ACTIONS.UPDATE_NESTED_FIELD:
      const [parent, child] = action.payload.name.split('.');
      const newNestedValue = action.payload.value;
      const originalNestedValue = state.originalData?.[parent]?.[child];
      
      const nestedIsChanged = newNestedValue !== originalNestedValue;
      const newNestedChangedFields = { ...state.changedFields };
      
      if (nestedIsChanged) {
        newNestedChangedFields[action.payload.name] = newNestedValue;
      } else {
        delete newNestedChangedFields[action.payload.name];
      }

      // Clear nested field error
      const nestedFieldErrors = { ...state.fieldErrors };
      delete nestedFieldErrors[action.payload.name];

      return {
        ...state,
        userData: {
          ...state.userData,
          [parent]: {
            ...state.userData[parent],
            [child]: newNestedValue
          }
        },
        changedFields: newNestedChangedFields,
        fieldErrors: nestedFieldErrors,
        isDirty: Object.keys(newNestedChangedFields).length > 0,
        error: null
      };

    case PROFILE_ACTIONS.SET_TEMP_IMAGE:
      // Clean up old blob URL
      if (state.tempImage && state.tempImage.startsWith('blob:')) {
        URL.revokeObjectURL(state.tempImage);
      }
      return {
        ...state,
        tempImage: action.payload,
        previewImage: action.payload,
        isDirty: true,
      };

    case PROFILE_ACTIONS.SET_PREVIEW_IMAGE:
      return {
        ...state,
        userData: {
          ...state.userData,
          profileImage: action.payload || state.userData?.profileImage,
        },
        previewImage: action.payload || state.userData?.profileImage,
        tempImage: null,
      };

    case PROFILE_ACTIONS.SET_DIRTY:
      return {
        ...state,
        isDirty: action.payload
      };

    case PROFILE_ACTIONS.SET_SAVING:
      return {
        ...state,
        isSaving: action.payload,
        error: action.payload ? null : state.error
      };

    case PROFILE_ACTIONS.SET_SAVE_SUCCESS:
      return {
        ...state,
        saveSuccess: action.payload,
        isDirty: action.payload ? false : state.isDirty,
        changedFields: action.payload ? {} : state.changedFields,
        fieldErrors: action.payload ? {} : state.fieldErrors,
        error: action.payload ? null : state.error
      };

    case PROFILE_ACTIONS.SET_ACTIVE_TAB:
      return {
        ...state,
        activeTab: action.payload
      };

    case PROFILE_ACTIONS.RESET_PROFILE:
      if (!state.originalData) return state;
      
      return {
        ...state,
        userData: { ...state.originalData },
        previewImage: state.originalData.profileImage || state.previewImage,
        changedFields: {},
        fieldErrors: {},
        isDirty: false,
        error: null,
        saveSuccess: false
      };

    case PROFILE_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        isSaving: false,
        saveSuccess: false
      };

    case PROFILE_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };

    case PROFILE_ACTIONS.SET_FIELD_ERRORS:
      return {
        ...state,
        fieldErrors: action.payload,
        isSaving: false,
        saveSuccess: false
      };

    case PROFILE_ACTIONS.CLEAR_FIELD_ERROR:
      const updatedFieldErrors = { ...state.fieldErrors };
      delete updatedFieldErrors[action.payload];
      return {
        ...state,
        fieldErrors: updatedFieldErrors
      };

    case PROFILE_ACTIONS.SET_PENDING_IMAGE_FILE:
      return {
        ...state,
        userData: {
          ...state.userData,
          pendingImageFile: action.payload 
        }
      };

    default:
      throw new Error(`Unhandled action type: ${action.type}`);
  }
}

// ✅ NEW: Action creator for merging changed fields
export const profileActions = {
  setProfileData: (data) => ({
    type: PROFILE_ACTIONS.SET_PROFILE_DATA,
    payload: data
  }),

  // ✅ NEW: Merge partial updates from backend
  mergeChangedFields: (changedFields) => ({
    type: PROFILE_ACTIONS.MERGE_CHANGED_FIELDS,
    payload: changedFields
  }),

  updateField: (name, value) => ({
    type: PROFILE_ACTIONS.UPDATE_FIELD,
    payload: { name, value }
  }),

  updateNestedField: (name, value) => ({
    type: PROFILE_ACTIONS.UPDATE_NESTED_FIELD,
    payload: { name, value }
  }),

  setPreviewImage: (image) => ({
    type: PROFILE_ACTIONS.SET_PREVIEW_IMAGE,
    payload: image
  }),

  setDirty: (isDirty) => ({
    type: PROFILE_ACTIONS.SET_DIRTY,
    payload: isDirty
  }),

  setSaving: (isSaving) => ({
    type: PROFILE_ACTIONS.SET_SAVING,
    payload: isSaving
  }),

  setSaveSuccess: (success) => ({
    type: PROFILE_ACTIONS.SET_SAVE_SUCCESS,
    payload: success
  }),

  setActiveTab: (tab) => ({
    type: PROFILE_ACTIONS.SET_ACTIVE_TAB,
    payload: tab
  }),

  resetProfile: () => ({
    type: PROFILE_ACTIONS.RESET_PROFILE
  }),

  setError: (error) => ({
    type: PROFILE_ACTIONS.SET_ERROR,
    payload: error
  }),

  clearError: () => ({
    type: PROFILE_ACTIONS.CLEAR_ERROR
  }),

  setFieldErrors: (errors) => ({
    type: PROFILE_ACTIONS.SET_FIELD_ERRORS,
    payload: errors
  }),

  clearFieldError: (fieldName) => ({
    type: PROFILE_ACTIONS.CLEAR_FIELD_ERROR,
    payload: fieldName
  }),
  
  setTempImage: (image) => ({
    type: PROFILE_ACTIONS.SET_TEMP_IMAGE,
    payload: image,
  }),

  setPendingImageFile: (file) => ({
    type: PROFILE_ACTIONS.SET_PENDING_IMAGE_FILE,
    payload: file
  }),
};
